/**
 * SemanticsPage — `/semantics`. Семантическое ядро (корп-сегмент): кластеры →
 * группы → фразы с частотностью. Источник — semantic-core-corporate.md (парсинг
 * на бэке). Сбор/расширение — через AI-чат (кнопки).
 */

import { useState } from 'react';
import { usePageHeader } from '@/components/layout/pageHeader';
import { Badge, Button, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { useAiStore } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { useSemantics, type SemCluster } from '@/hooks/api/useSemantics';

export function SemanticsPage() {
  usePageHeader({ title: 'Семантика', subtitle: 'Семантическое ядро и кластеры' });

  const { data, isLoading, isError, error, refetch } = useSemantics();
  const openPanel = useAiStore((s) => s.openPanel);
  const { sendMessage } = useAiChat();

  const ask = (q: string) => {
    openPanel();
    void sendMessage(q);
  };

  return (
    <div className="col gap16" style={{ maxWidth: 980 }}>
      <section className="card col" style={{ gap: 10 }}>
        <div className="row spread">
          <h3 style={{ margin: 0 }}>
            Ядро · корп-сегмент{data ? <span className="muted" style={{ fontWeight: 400 }}> · {data.total} фраз</span> : null}
          </h3>
        </div>
        <div className="row wrap" style={{ gap: 8 }}>
          <Button variant="outline" size="sm" onClick={() => ask('Собери спрос по кластеру «Коммерческие» через Wordstat и предложи новые фразы.')}>
            Собрать спрос (Wordstat)
          </Button>
          <Button variant="outline" size="sm" onClick={() => ask('Разбери отчёт «Поисковые запросы» и предложи минус-слова.')}>
            Поисковые запросы → минус-слова
          </Button>
          <Button variant="outline" size="sm" onClick={() => ask('Разложи собранные фразы по интент-кластерам и проставь операторы соответствия.')}>
            Кластеризовать
          </Button>
        </div>
      </section>

      {isLoading && <LoadingBlock label="Читаю семантическое ядро…" />}
      {isError && <ErrorBlock text={(error as Error)?.message} onRetry={() => void refetch()} />}
      {data?.clusters.map((c) => <ClusterCard key={c.title} cluster={c} />)}
      {data && !data.clusters.length && (
        <p className="muted">Ядро не распознано. Собрать через Wordstat — кнопки выше.</p>
      )}
    </div>
  );
}

function ClusterCard({ cluster }: { cluster: SemCluster }) {
  const count = cluster.groups.reduce((s, g) => s + g.phrases.length, 0);
  const [open, setOpen] = useState(true);
  return (
    <section className="card col" style={{ gap: 10 }}>
      <button
        type="button"
        className="row spread center"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, width: '100%' }}
        onClick={() => setOpen((v) => !v)}
      >
        <strong style={{ fontSize: 14, textAlign: 'left' }}>{cluster.title}</strong>
        <Badge tone="b-neutral">{count} фраз</Badge>
      </button>
      {open &&
        cluster.groups.map((g) => (
          <div key={g.title} className="col" style={{ gap: 4 }}>
            <div className="eyebrow" style={{ marginTop: 6 }}>{g.title}</div>
            <table className="tbl tbl--compact" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Фраза</th>
                  <th className="num" style={{ width: 90 }}>Частота</th>
                  <th>Заметка</th>
                </tr>
              </thead>
              <tbody>
                {g.phrases.map((p, i) => (
                  <tr key={`${p.phrase}-${i}`}>
                    <td>{p.phrase}</td>
                    <td className="num mono">{p.freq}</td>
                    <td className="muted" style={{ fontSize: 12 }}>{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
